import { NavLink } from 'react-router-dom';

import DashboardSwitcher from './DashboardSwitcher.jsx';

const CENTIER_LOGO =
  'https://www.centier.com/images/default-source/content-images/centier_bank_4c_adjustedhorizontal.svg';

const SEVERITY_ROWS = [
  { key: 'critical', label: 'Critical', color: '#E53E3E' },
  { key: 'high', label: 'High', color: '#DD6B20' },
  { key: 'medium', label: 'Medium', color: '#D69E2E' },
  { key: 'low', label: 'Low', color: '#38A169' },
];

const STATUS_ROWS = [
  { key: 'unreviewed', label: 'Unreviewed', color: '#4A90DB' },
  { key: 'in_progress', label: 'In Progress', color: '#FFAE1E' },
  { key: 'escalated', label: 'Escalated', color: '#F16C6C' },
  { key: 'pending_complete', label: 'Pndg Complete', color: '#1FA9C3' },
  { key: 'resolved', label: 'Resolved', color: '#4FB980' },
  { key: 'no_action_needed', label: 'No Action', color: '#CBD5E0' },
];

const ASSIGNEE_ROWS = [
  { key: 'Tiffani D.', label: 'Tiffani D.', color: '#014C8D' },
  { key: 'Abbey V.', label: 'Abbey V.', color: '#1FA9C3' },
  { key: 'Tara Z.', label: 'Tara Z.', color: '#FFAE1E' },
  { key: 'Emily B.', label: 'Emily B.', color: '#38A169' },
  { key: 'other', label: 'Other', color: '#805AD5' },
  { key: 'unassigned', label: 'Unassigned', color: 'rgba(255, 255, 255, 0.2)' },
];

export const QUICK_FILTERS = [
  {
    key: 'critical',
    icon: '🔴',
    label: 'Critical',
    filterKey: 'severity',
    filterValue: 'critical',
    countPath: (s) => s?.by_severity?.critical ?? 0,
  },
  {
    key: 'high',
    icon: '🟠',
    label: 'High',
    filterKey: 'severity',
    filterValue: 'high',
    countPath: (s) => s?.by_severity?.high ?? 0,
  },
  {
    key: 'unassigned',
    icon: '📋',
    label: 'Unassigned',
    filterKey: 'unassigned',
    filterValue: true,
    countPath: (s) => s?.by_assignee?.unassigned ?? 0,
  },
  {
    key: 'follow_up',
    icon: '👤',
    label: 'Follow-Up',
    filterKey: 'follow_up',
    filterValue: true,
    countPath: (s) => s?.follow_up_pending ?? 0,
  },
  {
    key: 'resolved',
    icon: '✅',
    label: 'Resolved',
    filterKey: 'status',
    filterValue: 'resolved',
    countPath: (s) => s?.by_status?.resolved ?? 0,
  },
];

function fmtValue(v) {
  if (v === null || v === undefined) return '—';
  return v;
}

function StatsBlock({ stats, loading }) {
  const rows = [
    ['Total Submissions', stats?.total, null],
    ['New Today', stats?.new_today, (v) => (v > 0 ? 'teal' : null)],
    ['Unresolved Crit/High', stats?.unresolved_critical_high, (v) => (v > 0 ? 'critical' : null)],
    ['Follow-Up Pending', stats?.follow_up_pending, (v) => (v > 0 ? 'accent' : null)],
    ['Avg Turnaround', stats?.avg_turnaround_display, null],
    ['Resolved This Week', stats?.resolved_this_week, null],
  ];
  return (
    <ul className="sb-stats">
      {rows.map(([label, value, highlightFn]) => {
        const highlight = highlightFn && value != null ? highlightFn(value) : null;
        const valueClass =
          'sb-stats__value' + (highlight ? ` sb-stats__value--${highlight}` : '');
        return (
          <li key={label} className="sb-stats__row">
            <span className="sb-stats__label">{label}</span>
            {loading ? (
              <span className="sb-stats__skeleton" aria-hidden="true" />
            ) : (
              <span className={valueClass}>{fmtValue(value)}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function MiniBarChart({ rows, source, loading, emptyText = 'No data yet' }) {
  const values = rows.map((r) => source?.[r.key] ?? 0);
  const max = Math.max(...values, 1);
  const total = values.reduce((s, v) => s + v, 0);

  if (loading) {
    return (
      <div className="sb-mini-chart">
        {rows.map((r) => (
          <div className="sb-mini-row" key={r.key}>
            <span className="sb-mini-label">{r.label}</span>
            <span className="sb-mini-track">
              <span className="sb-mini-fill sb-mini-fill--skeleton" />
            </span>
            <span className="sb-mini-count">—</span>
          </div>
        ))}
      </div>
    );
  }

  if (total === 0) {
    return <div className="sb-empty">{emptyText}</div>;
  }

  return (
    <div className="sb-mini-chart">
      {rows.map((r) => {
        const value = source?.[r.key] ?? 0;
        const pct = (value / max) * 100;
        return (
          <div className="sb-mini-row" key={r.key}>
            <span className="sb-mini-label">{r.label}</span>
            <span className="sb-mini-track">
              <span
                className="sb-mini-fill"
                style={{ width: `${pct}%`, background: r.color }}
              />
            </span>
            <span className="sb-mini-count">{value}</span>
          </div>
        );
      })}
    </div>
  );
}

function QuickFilters({ stats, loading, activeKey, onSelect }) {
  return (
    <ul className="sb-quick">
      {QUICK_FILTERS.map((q) => {
        const active = activeKey === q.key;
        const count = loading ? '—' : q.countPath(stats);
        return (
          <li key={q.key}>
            <button
              type="button"
              className={`sb-quick__item${active ? ' sb-quick__item--active' : ''}`}
              onClick={() => onSelect(q.key)}
              aria-pressed={active}
            >
              <span className="sb-quick__icon" aria-hidden="true">
                {q.icon}
              </span>
              <span className="sb-quick__label">{q.label}</span>
              <span className="sb-quick__count">{count}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function DashboardIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"
        fill="currentColor"
      />
    </svg>
  );
}

function ReportsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zM8 12h8v2H8zm0 4h5v2H8zM13 3.5L18.5 9H13z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * The primary navigation column. On the dashboard, `stats` + the
 * quick-filter callbacks are provided so the sidebar can render overview
 * metrics, mini charts, and clickable filter shortcuts. On other pages,
 * those props are omitted and only the logo + nav are shown.
 */
export default function Sidebar({
  stats,
  statsLoading = false,
  activeQuickFilter = null,
  onQuickFilter,
}) {
  const showDashboardExtras = onQuickFilter != null;

  return (
    <aside className="sidebar">
      <div className="sidebar__header">
        <img className="sidebar__logo" src={CENTIER_LOGO} alt="Centier Bank" />
      </div>

      {/* Users spanning multiple dashboards get the shared tab switcher. */}
      <DashboardSwitcher />

      <div className="sidebar__scroll">
        {showDashboardExtras && (
          <>
            <div className="sidebar__section">
              <div className="sidebar__section-label">Overview</div>
              <StatsBlock stats={stats} loading={statsLoading} />
            </div>

            <div className="sidebar__section">
              <div className="sidebar__section-label">Severity</div>
              <MiniBarChart
                rows={SEVERITY_ROWS}
                source={stats?.by_severity}
                loading={statsLoading}
              />
            </div>

            <div className="sidebar__section">
              <div className="sidebar__section-label">Status</div>
              <MiniBarChart
                rows={STATUS_ROWS}
                source={stats?.by_status}
                loading={statsLoading}
              />
            </div>

            <div className="sidebar__section">
              <div className="sidebar__section-label">Open Assignments</div>
              <MiniBarChart
                rows={ASSIGNEE_ROWS.filter(
                  (r) =>
                    r.key !== 'other' ||
                    (stats?.by_assignee?.other ?? 0) > 0
                )}
                source={stats?.by_assignee}
                loading={statsLoading}
                emptyText="No open assignments"
              />
            </div>

            <div className="sidebar__section">
              <div className="sidebar__section-label">Quick Filters</div>
              <QuickFilters
                stats={stats}
                loading={statsLoading}
                activeKey={activeQuickFilter}
                onSelect={onQuickFilter}
              />
            </div>
          </>
        )}
      </div>

      <nav className="sidebar__footer" aria-label="Primary">
        <NavLink
          to="/dashboard"
          className={({ isActive }) =>
            'sidebar__link' + (isActive ? ' sidebar__link--active' : '')
          }
        >
          <DashboardIcon />
          Dashboard
        </NavLink>
        <NavLink
          to="/reports"
          className={({ isActive }) =>
            'sidebar__link' + (isActive ? ' sidebar__link--active' : '')
          }
        >
          <ReportsIcon />
          Reports
        </NavLink>
      </nav>
    </aside>
  );
}
